// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { isNil } from 'lodash-es';
import { getErrorMessage } from '@/utils/errors';
import { RootState } from '../index';

/**
 * AI credits — the per-action cost table, the live exchange rate, and the rail to buy more.
 *
 * Ownership after the owner-hub wallet integration:
 *  - The BALANCE is an owner-level virtual-currency wallet on the owner-hub wallet service
 *    (`wallet_type AI_CREDITS`, currency `AIC`, 1 credit = 1 AIC), read off `state.ownerWallet.credits`
 *    (derived from `/wallet/list`). This slice does NOT hold a balance.
 *  - The per-action COST (how many credits a generate/restyle/… draw costs) is owned by
 *    spark-creatives and served at `${creativesApiUrl}/credits/pricing` (`{ actions }`). It drives the
 *    cost chips + the client-side pre-flight gate; the authoritative charge is spark's server 402.
 *  - The exchange RATE (fiat per credit) is owned by owner-hub and quoted by
 *    `POST /invoice/deposit/query-ai-credits`. It drives the buy UI's money<->credits conversions.
 *  - BUYING credits mints a `deposit_ai_credits` invoice (`POST /invoice/deposit/ai-credits`), settled
 *    by card (its Stripe `provider_reference`) or from the MAIN wallet (`payInvoiceFromWallet`). On paid
 *    the owner-hub wallet credits the AI_CREDITS balance; the buy UI polls it until it moves.
 */

const DEBUG = false;

/** Per-action COST table from spark-creatives (`…/api/spark-creatives/v1/credits`). '' when unconfigured. */
const sparkCreditsBase = (): string => {
  const url = (window.config as unknown as Record<string, unknown> | undefined)?.creativesApiUrl;
  return typeof url === 'string' ? `${url.replace(/\/+$/, '')}/credits` : '';
};

/** Per-action COST table from spark-blog (`{blogApiUrl}/credits`). '' only when the key is
 *  ABSENT: an empty-string blogApiUrl passes the typeof test and yields a relative
 *  '/credits', which is why the chart gates the key rather than defaulting it to ''. */
const sparkBlogCreditsBase = (): string => {
  const url = (window.config as unknown as Record<string, unknown> | undefined)?.blogApiUrl;
  return typeof url === 'string' ? `${url.replace(/\/+$/, '')}/credits` : '';
};

/** owner-hub user invoice base (`${userApiUrl}/invoice`) — the rate quote + the AI-credits deposit invoice. */
const invoiceBase = (): string => `${window.config.userApiUrl.replace(/\/+$/, '')}/invoice`;

type GenericError = {
  rejectValue: {
    error: string;
  };
};

const getAuthHeaders = (state: RootState): Record<string, string> | null => {
  const authToken = state.authentication.access_token;
  if (isNil(authToken)) return null;
  return { Authorization: authToken };
};

/** Credits/counts/rates arrive from the API as numbers or strings; coerce to a finite number. */
const toNumber = (value: unknown): number => {
  const n = parseFloat(String(value ?? ''));
  return Number.isFinite(n) ? n : 0;
};

/* ---------- contract shapes ---------- */

/** A known AI action's pricing key. Kept open so a new backend action still resolves a cost. */
export type CreditActionKey =
  | 'generate'
  | 'variations'
  | 'restyle'
  | 'custom'
  | 'upload_place'
  | 'video'
  | 'video_beat'
  | string;

/** A known BLOG AI action's pricing key (spark-blog's own price table). Kept open like the above. */
export type BlogCreditActionKey = 'article' | 'body' | 'seo' | 'hero' | 'quiz' | string;

/** `GET ${creativesApiUrl}/credits/pricing` — the per-action credit cost table (spark-owned). */
export interface CreditPricing {
  actions: Record<CreditActionKey, number>;
}

/**
 * `POST /invoice/deposit/ai-credits { ai_credits_amount, currency }` — mints a `deposit_ai_credits`
 * invoice priced in fiat and returns the settlement rails: `invoice_id` (payable from the MAIN wallet
 * via `payInvoiceFromWallet`) and the Stripe PaymentIntent client secret on `provider_reference` (for
 * the card rail). `price` is the fiat amount the wallet/card is charged.
 */
export interface CreateAiCreditsInvoiceResult {
  invoice_id: string;
  clientSecret?: string;
  price: number;
  currency: string;
}

/* ---------- pure helpers ---------- */

/** Look up an action's credit cost from the loaded price table; undefined until priced. */
export const creditCostFor = (
  pricing: CreditPricing | null | undefined,
  action: CreditActionKey,
): number | undefined => {
  const cost = pricing?.actions?.[action];
  return typeof cost === 'number' && Number.isFinite(cost) ? cost : undefined;
};

/**
 * The per-beat storyboard credit cost for a given video model: the model's own price row
 * (`video_beat:<model>`) when priced, else the flat `video_beat` fallback. Mirrors the backend charge
 * (spark-creatives `storyboardBeatCost`), so the Studio's up-front estimate matches what is actually
 * charged. Undefined only when neither is priced.
 */
export const videoBeatCostFor = (
  pricing: CreditPricing | null | undefined,
  model: string | undefined,
): number | undefined =>
  (model ? creditCostFor(pricing, `video_beat:${model}`) : undefined) ?? creditCostFor(pricing, 'video_beat');

/**
 * The credit cost of ONE banner-kit render (the hero, one shape, a mini-game background plate, or a kit layer
 * text/restyle edit) on a given image model: its `kit_render:<model>` row. No flat fallback: the backend fails
 * closed on a missing row (409), so an unpriced model reads undefined and the cost chip stays hidden.
 */
export const kitRenderCostFor = (pricing: CreditPricing | null | undefined, model: string | undefined): number | undefined =>
  model ? creditCostFor(pricing, `kit_render:${model}`) : undefined;

/** The credit cost of one mini-game sprite sheet on a given image model (`playable_sprite:<model>`). No flat
 *  fallback, like {@link kitRenderCostFor}. */
export const playableSpriteCostFor = (
  pricing: CreditPricing | null | undefined,
  model: string | undefined,
): number | undefined => (model ? creditCostFor(pricing, `playable_sprite:${model}`) : undefined);

/** The credit cost of decomposing one kit shape into layers on a given model (`kit_layers:<model>`). No flat
 *  fallback, like {@link kitRenderCostFor}. */
export const kitLayersCostFor = (pricing: CreditPricing | null | undefined, model: string | undefined): number | undefined =>
  model ? creditCostFor(pricing, `kit_layers:${model}`) : undefined;

/* ---------- slice state ---------- */

export interface AiCreditsState {
  /** Per-action credit cost table (spark-owned) — drives cost chips + the client pre-flight gate. */
  pricing: CreditPricing | null;
  pricingLoading: boolean;
  pricingLoaded: boolean;
  pricingError: string | null | undefined;

  /** Per-action credit cost table for BLOG generations (spark-blog-owned) — same role as `pricing`,
   *  separate table because it is a separate service with its own actions (article/body/seo/hero/quiz). */
  blogPricing: CreditPricing | null;
  blogPricingLoading: boolean;
  blogPricingLoaded: boolean;
  blogPricingError: string | null | undefined;

  /** Live exchange rate, USD per credit (owner-hub-owned) — drives the buy UI's money<->credits
   *  conversions. `null` until quoted; the helpers in nova/utils/credits.ts fall back to the constant. */
  rateUsd: number | null;
  rateLoading: boolean;
  rateLoaded: boolean;
  rateError: string | null | undefined;

  /** A `deposit_ai_credits` invoice mint is in flight. */
  purchasing: boolean;
  purchaseError: string | null | undefined;
}

/* ---------- thunks ---------- */

/** Per-action credit COST table from spark-creatives (`{ actions }`). */
export const fetchCreditsPricing = createAsyncThunk<CreditPricing, void, GenericError>(
  'aiCredits/pricing',
  async (_, { rejectWithValue, getState }) => {
    try {
      const base = sparkCreditsBase();
      if (!base) return rejectWithValue({ error: 'Creatives service is not configured' });
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      // A 401 from the separate spark service must never rotate the owner-hub token.
      const response = await axios.get(`${base}/pricing`, { headers, _owsNoAuthRecovery: true });
      DEBUG && console.log('[AI Credits] pricing response', response);

      const data = response.data as { actions?: Record<string, number> } | undefined;
      if (data && typeof data.actions === 'object' && data.actions !== null) {
        return { actions: data.actions };
      }
      return rejectWithValue({ error: "Couldn't fetch credit pricing" });
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/** Per-action credit COST table for blog generations, from spark-blog (`{ actions }`). */
export const fetchBlogCreditsPricing = createAsyncThunk<CreditPricing, void, GenericError>(
  'aiCredits/blogPricing',
  async (_, { rejectWithValue, getState }) => {
    try {
      const base = sparkBlogCreditsBase();
      if (!base) return rejectWithValue({ error: 'The blog service is not configured' });
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      // A 401 from the separate spark service must never rotate the owner-hub token.
      const response = await axios.get(`${base}/pricing`, { headers, _owsNoAuthRecovery: true });
      DEBUG && console.log('[AI Credits] blog pricing response', response);

      const data = response.data as { actions?: Record<string, number> } | undefined;
      if (data && typeof data.actions === 'object' && data.actions !== null) {
        return { actions: data.actions };
      }
      return rejectWithValue({ error: "Couldn't fetch credit pricing" });
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/**
 * Live exchange rate (USD per credit) from owner-hub's stateless quote endpoint. Quoting 1 credit
 * returns the current `exchange_rate`; the invoice is repriced authoritatively at creation time, so
 * this only drives the pre-purchase preview.
 */
export const fetchCreditRate = createAsyncThunk<number, void, GenericError>(
  'aiCredits/rate',
  async (_, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      // Priced/funded in USD (the credit rate is USD-denominated), matching CREDIT_CURRENCY.
      // This hits the OWNER-HUB (`${userApiUrl}/invoice/*`), so it must keep the standard refresh-on-401
      // recovery (unlike the spark `/credits/*` calls): a token that expired while the Buy sheet was open
      // is refreshed + replayed instead of failing the quote outright.
      const response = await axios.post(`${invoiceBase()}/deposit/query-ai-credits`, { ai_credits_amount: 1, currency: 'USD' }, { headers });
      DEBUG && console.log('[AI Credits] rate response', response);

      const data = response.data as { exchange_rate?: number | string; amount?: number | string } | undefined;
      // Prefer the explicit rate; fall back to the quoted amount for 1 credit.
      const rate = toNumber(data?.exchange_rate ?? data?.amount);
      if (rate > 0) return rate;
      return rejectWithValue({ error: "Couldn't read the credit exchange rate" });
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/**
 * Mint a `deposit_ai_credits` invoice for `ai_credits_amount` credits, priced in `currency`. Returns
 * both settlement rails; the Buy-credits flow settles it from the MAIN wallet when funds cover it, or
 * confirms the Stripe intent (the card rail) otherwise. On paid the owner-hub wallet credits the
 * balance; the caller polls the wallet until the new credits land.
 */
export const createAiCreditsInvoice = createAsyncThunk<
  CreateAiCreditsInvoiceResult,
  { ai_credits_amount: number; currency?: string },
  GenericError
>(
  'aiCredits/createInvoice',
  async ({ ai_credits_amount, currency }, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      // OWNER-HUB invoice mint (`${userApiUrl}/invoice/*`) — keep the standard refresh-on-401 recovery
      // (as wallet.ts's /invoice/deposit does), so a token that expired while the sheet was open is
      // refreshed + replayed rather than failing the purchase until a manual reload.
      const response = await axios.post(`${invoiceBase()}/deposit/ai-credits`, { ai_credits_amount, currency: currency || 'USD' }, { headers });
      DEBUG && console.log('[AI Credits] create invoice response', response);

      const data = response.data as
        | { id?: string; provider_reference?: string; client_secret?: string; price?: number | string; currency?: string }
        | undefined;
      const invoiceId = data?.id;
      if (!invoiceId) return rejectWithValue({ error: 'Deposit did not return an invoice' });
      return {
        invoice_id: invoiceId,
        clientSecret: data?.provider_reference ?? data?.client_secret,
        price: toNumber(data?.price),
        currency: data?.currency || currency || 'USD',
      };
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

const initialState: AiCreditsState = {
  pricing: null,
  pricingLoading: false,
  pricingLoaded: false,
  pricingError: null,

  blogPricing: null,
  blogPricingLoading: false,
  blogPricingLoaded: false,
  blogPricingError: null,

  rateUsd: null,
  rateLoading: false,
  rateLoaded: false,
  rateError: null,

  purchasing: false,
  purchaseError: null,
};

export const aiCreditsSlice = createSlice({
  name: 'aiCreditsSlice',
  initialState,
  reducers: {
    /**
     * Wipe credits state back to empty. Owner-scoped (resolved server-side from the token),
     * so it must not outlive its session — dispatched by BOTH session-teardown chokepoints in
     * `store/index.ts` (logout + an impersonation identity change), mirroring `resetWallet`.
     */
    resetAiCredits: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      /* ----- per-action cost (spark /credits/pricing) ----- */
      .addCase(fetchCreditsPricing.pending, (state) => {
        state.pricingLoading = true;
        state.pricingError = null;
      })
      .addCase(fetchCreditsPricing.fulfilled, (state, action) => {
        state.pricingLoading = false;
        state.pricingLoaded = true;
        state.pricing = action.payload;
      })
      .addCase(fetchCreditsPricing.rejected, (state, action) => {
        state.pricingLoading = false;
        state.pricingLoaded = true;
        state.pricingError = action.payload?.error;
      })

      /* ----- per-action cost for blog generations (spark-blog /credits/pricing) ----- */
      .addCase(fetchBlogCreditsPricing.pending, (state) => {
        state.blogPricingLoading = true;
        state.blogPricingError = null;
      })
      .addCase(fetchBlogCreditsPricing.fulfilled, (state, action) => {
        state.blogPricingLoading = false;
        state.blogPricingLoaded = true;
        state.blogPricing = action.payload;
      })
      .addCase(fetchBlogCreditsPricing.rejected, (state, action) => {
        state.blogPricingLoading = false;
        state.blogPricingLoaded = true;
        state.blogPricingError = action.payload?.error;
      })

      /* ----- exchange rate (owner-hub query-ai-credits) ----- */
      .addCase(fetchCreditRate.pending, (state) => {
        state.rateLoading = true;
        state.rateError = null;
      })
      .addCase(fetchCreditRate.fulfilled, (state, action) => {
        state.rateLoading = false;
        state.rateLoaded = true;
        state.rateUsd = action.payload;
      })
      .addCase(fetchCreditRate.rejected, (state, action) => {
        state.rateLoading = false;
        state.rateLoaded = true;
        state.rateError = action.payload?.error;
      })

      /* ----- deposit_ai_credits invoice mint ----- */
      .addCase(createAiCreditsInvoice.pending, (state) => {
        state.purchasing = true;
        state.purchaseError = null;
      })
      .addCase(createAiCreditsInvoice.fulfilled, (state) => {
        state.purchasing = false;
      })
      .addCase(createAiCreditsInvoice.rejected, (state, action) => {
        state.purchasing = false;
        state.purchaseError = action.payload?.error;
      });
  },
});

export const { resetAiCredits } = aiCreditsSlice.actions;

export default aiCreditsSlice.reducer;
